import { SEGMENT_COUNT, SEGMENT_SLICE } from './wheelConfig';

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** CSS-positive rotation is clockwise. 0° keeps segment 0's start at 12 o'clock. */
export function discAngleUnderPointer(rotationDeg: number): number {
  return ((-rotationDeg) % 360 + 360) % 360;
}

export function segmentIndexFromRotation(rotationDeg: number, count = SEGMENT_COUNT): number {
  const slice = 360 / count;
  const angle = discAngleUnderPointer(rotationDeg);
  const index = Math.floor((angle + 1e-6) / slice);
  return ((index % count) + count) % count;
}

export function clampJitter(slice: number, jitter: number): number {
  const limit = slice / 2 - 2.4;
  if (limit <= 0) return 0;
  return Math.max(-limit, Math.min(limit, jitter));
}

export function targetRotationForSegment(
  current: number,
  segmentIndex: number,
  extraTurns = 6,
  jitterDeg = 0,
  count = SEGMENT_COUNT,
): number {
  const slice = 360 / count;
  const center = segmentIndex * slice + slice / 2;
  const landing = (360 - (center + clampJitter(slice, jitterDeg))) % 360;
  const currentMod = ((current % 360) + 360) % 360;
  let delta = landing - currentMod;
  if (delta <= 0.5) delta += 360;
  return current + extraTurns * 360 + delta;
}

/**
 * Fast start, long luxurious stop. Distance is mapped with a split curve so
 * the first ~12% of time covers a small run-up, then quintic ease-out takes over.
 */
export function wheelEase(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  if (x < 0.08) {
    const u = x / 0.08;
    return 0.11 * u * u;
  }
  const u = (x - 0.08) / 0.92;
  return 0.11 + 0.89 * (1 - (1 - u) ** 4.4);
}

export interface SpinFrame {
  rotation: number;
  progress: number;
  segmentIndex: number;
}

export function animateSpin(options: {
  from: number;
  to: number;
  durationMs: number;
  reducedMotion?: boolean;
  onFrame: (frame: SpinFrame) => void;
  onComplete: (rotation: number) => void;
}): () => void {
  const reduced = options.reducedMotion ?? prefersReducedMotion();
  const duration = reduced ? Math.min(650, options.durationMs) : options.durationMs;
  const ease = reduced ? (t: number) => t : wheelEase;
  let cancelled = false;
  let raf = 0;
  const start = performance.now();

  const tick = (now: number) => {
    if (cancelled) return;
    const t = Math.min(1, (now - start) / duration);
    const rotation = options.from + (options.to - options.from) * ease(t);
    options.onFrame({
      rotation,
      progress: t,
      segmentIndex: segmentIndexFromRotation(rotation),
    });
    if (t < 1) {
      raf = requestAnimationFrame(tick);
      return;
    }
    options.onComplete(options.to);
  };

  raf = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}

export const SLICE_DEG = SEGMENT_SLICE;
